/**
 * WeatherCard — the seven weathers and the types each boosts (plan phase 4, decision 0020).
 *
 * The backend's ``weather_card`` carries the whole table with the asked
 * weather marked, the type or Pokémon asked about, and what a boost does.
 * Rendered by CardRenderer.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }
interface Row { weather: string; types: string[] }

function TypeChip({ type, highlight }: { type: string; highlight: boolean }) {
  const cls = TYPE_BADGE_COLORS[type.toLowerCase()] ?? "bg-white/10 text-white/60";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls} ${highlight ? "ring-1 ring-white/70" : ""}`}>{type}</span>;
}

export default function WeatherCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "weather") as Record<string, unknown> | undefined;
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">Not a weather I know.</p>
      </CardShell>
    );
  }
  const table = Array.isArray(sec.table) ? (sec.table as Row[]) : [];
  const asked = typeof sec.weather === "string" ? sec.weather : null;
  const askedType = typeof sec.asked_type === "string" ? sec.asked_type : null;
  const pokemonWeathers = (sec.pokemon_weathers ?? {}) as Record<string, string>;
  const pokemonName = typeof sec.pokemon_name === "string" ? sec.pokemon_name : null;
  const notes = Array.isArray(sec.notes) ? (sec.notes as string[]) : [];
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-3">
        {pokemonName && (
          <p className="text-xs text-white/70">
            {pokemonName}: {Object.entries(pokemonWeathers).map(([t, w]) => `${t} → ${w}`).join(" · ")}
          </p>
        )}
        <div className="space-y-1">
          {table.map((row) => {
            const on = asked === row.weather;
            return (
              <div key={row.weather} className={`flex items-center justify-between rounded-lg px-2 py-1.5 ${on ? "bg-white/[0.08] border border-white/[0.12]" : ""}`}>
                <span className={`text-xs ${on ? "text-white font-semibold" : "text-white/60"}`}>{row.weather}</span>
                <span className="flex gap-1">
                  {row.types.map((t) => <TypeChip key={t} type={t} highlight={askedType === t || t in pokemonWeathers} />)}
                </span>
              </div>
            );
          })}
        </div>
        {notes.length > 0 && (
          <ul className="space-y-1">
            {notes.map((n) => <li key={n} className="text-[11px] text-white/45">{n}</li>)}
          </ul>
        )}
      </div>
    </CardShell>
  );
}
